// src/components/auth/AuthLayout.jsx
// Shared split-screen shell for the app's auth doors (/signin, /login).
// Left: a branded marketing panel (hidden on small screens). Right: the form.
// The `accent` prop keeps the two doors visual siblings while distinguishing
// the student door (indigo/sky) from the staff door (slate/blue).
import React from "react";
import { motion } from "framer-motion";
import { HiAcademicCap, HiShieldCheck, HiCheckCircle } from "react-icons/hi";

const ACCENTS = {
  student: {
    gradient: "from-indigo-500 via-blue-500 to-sky-400",
    Icon: HiAcademicCap,
    chipBg: "bg-indigo-100",
    chipText: "text-indigo-600",
  },
  admin: {
    gradient: "from-slate-800 via-slate-700 to-blue-600",
    Icon: HiShieldCheck,
    chipBg: "bg-slate-100",
    chipText: "text-slate-700",
  },
};

// Floating math glyphs — same playful motif used on the MCQ browse page.
const GLYPHS = ["π", "∑", "√", "∞", "Δ", "θ", "%", "÷"];

export default function AuthLayout({
  accent = "student",
  panelTitle,
  panelSubtitle,
  bullets = [],
  children,
}) {
  const theme = ACCENTS[accent] || ACCENTS.student;
  const { Icon } = theme;

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* ---------- Brand panel (desktop only) ---------- */}
      <div
        className={`hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br ${theme.gradient} text-white`}
      >
        {/* Floating glyphs */}
        <div className="absolute inset-0 pointer-events-none select-none">
          {GLYPHS.map((g, i) => (
            <motion.span
              key={g}
              className="absolute text-white/10 font-extrabold"
              style={{
                left: `${(i * 13 + 8) % 90}%`,
                top: `${(i * 23 + 10) % 85}%`,
                fontSize: `${2 + (i % 4)}rem`,
              }}
              animate={{ y: [0, -14, 0], opacity: [0.08, 0.2, 0.08] }}
              transition={{ duration: 5 + (i % 4), repeat: Infinity, delay: i * 0.4 }}
            >
              {g}
            </motion.span>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full"
        >
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center">
              <Icon className="text-2xl" />
            </div>
            <span className="font-extrabold text-xl tracking-tight">Ravi TestPapers</span>
          </div>

          <div className="max-w-md">
            <h1 className="text-4xl xl:text-5xl font-extrabold leading-tight">{panelTitle}</h1>
            {panelSubtitle && (
              <p className="mt-4 text-white/80 text-lg leading-relaxed">{panelSubtitle}</p>
            )}

            {bullets.length > 0 && (
              <ul className="mt-8 space-y-3">
                {bullets.map((b, i) => (
                  <motion.li
                    key={b}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + i * 0.12 }}
                    className="flex items-center gap-3 text-white/90"
                  >
                    <HiCheckCircle className="text-xl text-white shrink-0" />
                    <span>{b}</span>
                  </motion.li>
                ))}
              </ul>
            )}
          </div>

          <p className="text-white/60 text-sm">
            © {new Date().getFullYear()} Ravi TestPapers. All rights reserved.
          </p>
        </motion.div>
      </div>

      {/* ---------- Form panel ---------- */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-10 sm:px-8">
        {/* Compact logo header — mobile only (brand panel is hidden there) */}
        <div className="lg:hidden flex items-center gap-2 mb-8">
          <div className={`w-10 h-10 rounded-xl ${theme.chipBg} flex items-center justify-center`}>
            <Icon className={`text-xl ${theme.chipText}`} />
          </div>
          <span className="font-extrabold text-lg text-gray-800">Ravi TestPapers</span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          {children}
        </motion.div>
      </div>
    </div>
  );
}
